/**
 * Wave 34 · Content overrides hook for public pages.
 *
 * Each public React page calls `useContentOverrides(slug)` to fetch
 * the published (or draft, if `?preview-draft=1`) content values for
 * its editable fields.  The hook returns a `get(path, fallback)` helper:
 *
 *     const c = useContentOverrides("services");
 *     <h1>{c.get("hero.title", "What We Do")}</h1>
 *
 * The React page hardcodes every fallback so the canonical content
 * ships in the bundle.  Network failures, missing overrides, or
 * unauthenticated draft fetches all silently fall through to the
 * hardcoded value — the public site never breaks just because the API
 * is slow.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function readPath(obj, dotted) {
  if (!obj || typeof obj !== "object") return undefined;
  const parts = dotted.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[p];
  }
  return cur;
}

export function useContentOverrides(slug) {
  const [values, setValues] = useState(null);
  const [mode, setMode] = useState("published");
  const previewMode = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get("preview-draft") === "1";
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const endpoint = previewMode
      ? `/api/site-content/public/${slug}/draft`
      : `/api/site-content/public/${slug}`;
    axios
      .get(`${API_BASE}${endpoint}`, { withCredentials: previewMode })
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setValues(data.values || {});
        setMode(data.mode || "published");
      })
      .catch(() => {
        // Silent fallback — React page's hardcoded defaults still render.
        if (!cancelled) setValues({});
      });
    return () => {
      cancelled = true;
    };
  }, [slug, previewMode]);

  const get = (path, fallback) => {
    if (!values) return fallback;
    const v = readPath(values, path);
    if (v === undefined || v === null) return fallback;
    return v;
  };

  return { get, mode, previewMode, loaded: values !== null };
}
